import { useEffect } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { z } from "zod";
import {
  MiBox,
  MiDetailFields,
  MiDetailPageHeader,
  MiSectionHeader,
} from "@miplatform/components";
import {
  CONTACT_STATUSES_OPTIONS,
  INPUT_TYPES,
  PREFERRED_CONTACT_METHOD_OPTIONS,
} from "@miplatform/constants";
import { useEdit, useRouter } from "@miplatform/hooks";
import { getPhoneNumber } from "@miplatform/utils";
import { mdiChevronLeft, mdiPencilOutline } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { capitalize } from "@mui/material";
import { useContactDetailsViewModel } from "./useContactDetailsViewModel";

export const ContactDetails = () => {
  const route = useRouter();
  const { contactId, accountId } = route.params;
  const edit = useEdit({});
  const {
    isLoading,
    isEditing,
    contact,
    getContact,
    updateContact,
    handleEditOn,
    handleEditOff,
  } = useContactDetailsViewModel({ edit });

  const contactFields = [
    {
      label: "Account",
      name: "accountName",
      required: true,
      readOnly: true,
      width: "50%",
      validationRule: z.string().min(1, "Account is required"),
    },
    {
      label: "Title",
      name: "title",
      width: "50%",
      readOnly: !isEditing,
    },
    {
      label: "First Name",
      name: "firstName",
      required: true,
      readOnly: !isEditing,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "First name is required"),
    },
    {
      label: "Last Name",
      name: "lastName",
      required: true,
      readOnly: !isEditing,
      width: "50%",
      beforeOnChange: capitalize,
      validationRule: z.string().min(1, "Last name is required"),
    },
    {
      label: "Email",
      name: "email",
      required: true,
      readOnly: !isEditing,
      width: "50%",
      validationRule: z
        .string()
        .min(1, "Email is required")
        .email("Please enter a valid email address"),
    },
    {
      label: "Phone Number",
      name: "phone",
      value: getPhoneNumber(edit.getValue("phone")),
      required: true,
      readOnly: !isEditing,
      width: "50%",
      handleChange: (e) => {
        if (
          validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
          "TOO_LONG"
        ) {
          return;
        }

        edit.update({
          phone: getPhoneNumber(e.target.value),
        });
      },
      validationRule: z
        .string()
        .min(1, "Phone number is required")
        .refine(
          (value) => validatePhoneNumberLength(value, "US") === undefined,
          {
            message: "Please enter a valid phone number",
          }
        ),
    },
    {
      label: "Status",
      name: "status",
      width: "50%",
      readOnly: !isEditing,
      fieldType: INPUT_TYPES.SELECT,
      options: CONTACT_STATUSES_OPTIONS,
      validationRule: z.string().min(1, "Preferred contact method is required"),
    },
    {
      label: "Preferred Method",
      name: "preferredContactMethod",
      width: "50%",
      readOnly: !isEditing,
      fieldType: INPUT_TYPES.SELECT,
      options: PREFERRED_CONTACT_METHOD_OPTIONS,
      validationRule: z.string().min(1, "Preferred contact method is required"),
    },
    {
      label: "VIP",
      name: "isVip",
      readOnly: !isEditing,
      fieldType: INPUT_TYPES.CHECKBOX,
      width: "auto",
      validationRule: z.boolean().optional(),
    },
  ];

  edit.setValidation(contactFields);

  useEffect(() => {
    getContact({ accountId, contactId });
  }, []);

  useEffect(() => {
    if (route.location.state?.isEditing) {
      handleEditOn();
    }
  }, [route.location.state]);

  return (
    <>
      <MiDetailPageHeader
        title={`Contact: ${contact?.displayName || ""}`}
        titleIcon={mdiChevronLeft}
        loading={isLoading}
      />
      <MiBox height="100%" overflow="auto">
        <MiSectionHeader
          title="Contact Details"
          rightIcon={!isEditing && mdiPencilOutline}
          onRightIconClick={handleEditOn}
        />
        <MiDetailFields detailFields={contactFields} edit={edit} />
      </MiBox>
      <Footer
        show={isEditing}
        handleSaveClick={updateContact}
        handleCancelClick={handleEditOff}
        disableSave={!(edit.isValid() && edit.isDirty()) || isLoading}
      />
    </>
  );
};
